import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { apiDelete, apiGet, apiPut } from "@/lib/api";

export const Route = createFileRoute("/_authenticated/admin/recipients")({
  component: RecipientsPage,
});

interface Rule {
  userId: string;
  name: string;
  email: string;
  allDocs: boolean;
  docTypes: string[];
  allSites: boolean;
  projectIds: string[];
}
interface RecipientsData {
  docKinds: { key: string; label: string }[];
  projects: { id: string; name: string }[];
  candidates: { userId: string; name: string; email: string; role: string }[];
  rules: Rule[];
}

function RecipientsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "doc-recipients"],
    queryFn: () => apiGet<{ data: RecipientsData }>("/api/admin/doc-recipients"),
  });
  const [editing, setEditing] = useState<Rule | "new" | null>(null);
  const info = data?.data;

  const remove = useMutation({
    mutationFn: (userId: string) => apiDelete(`/api/admin/doc-recipients/${userId}`),
    onSuccess: () => {
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["admin", "doc-recipients"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const kindLabel = (k: string) => info?.docKinds.find((x) => x.key === k)?.label ?? k;
  const siteName = (id: string) => info?.projects.find((p) => p.id === id)?.name ?? "a site";

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Document recipients</h1>
          <p className="text-sm text-muted-foreground">
            Paris team members who get a copy of the signed documents by email. Choose which documents and which sites for each person.
          </p>
        </div>
        <Button className="gap-1" onClick={() => setEditing("new")} disabled={!info}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>

      {isLoading && <Skeleton className="h-24 w-full rounded-xl" />}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {info && info.rules.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <Mail className="mb-2 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Nobody is set up yet. Add a person to start sending them copies.</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {info?.rules.map((r) => (
          <Card key={r.userId}>
            <CardContent className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold">{r.name}</p>
                <p className="truncate text-xs text-muted-foreground">{r.email}</p>
                <p className="mt-1 text-xs">
                  <span className="font-medium">Documents:</span> {r.allDocs ? "every document" : r.docTypes.map(kindLabel).join(", ")}
                </p>
                <p className="text-xs">
                  <span className="font-medium">Sites:</span> {r.allSites ? "every site" : r.projectIds.map(siteName).join(", ")}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setEditing(r)}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1 text-xs text-destructive"
                  onClick={() => window.confirm(`Stop sending documents to ${r.name}?`) && remove.mutate(r.userId)}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {info && <RuleForm info={info} editing={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function RuleForm({ info, editing, onClose }: { info: RecipientsData; editing: Rule | "new" | null; onClose: () => void }) {
  const qc = useQueryClient();
  const existing = editing && editing !== "new" ? editing : null;
  const [userId, setUserId] = useState("");
  const [allDocs, setAllDocs] = useState(true);
  const [docTypes, setDocTypes] = useState<string[]>([]);
  const [allSites, setAllSites] = useState(true);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [siteSearch, setSiteSearch] = useState("");
  const [loadedFor, setLoadedFor] = useState<unknown>(null);

  // Load the form when the dialog opens for a different person
  if (editing !== loadedFor) {
    setLoadedFor(editing);
    setUserId(existing?.userId ?? "");
    setAllDocs(existing?.allDocs ?? true);
    setDocTypes(existing?.docTypes ?? []);
    setAllSites(existing?.allSites ?? true);
    setProjectIds(existing?.projectIds ?? []);
    setSiteSearch("");
  }

  const save = useMutation({
    mutationFn: () => apiPut(`/api/admin/doc-recipients/${userId}`, { allDocs, docTypes, allSites, projectIds }),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["admin", "doc-recipients"] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const shownSites = info.projects.filter((p) => !siteSearch || p.name.toLowerCase().includes(siteSearch.toLowerCase()));

  return (
    <Dialog open={!!editing} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogTitle>{existing ? existing.name : "Add a recipient"}</DialogTitle>
        <DialogDescription>They receive the signed document by email, with the PDF attached.</DialogDescription>
        <div className="space-y-4">
          {!existing && (
            <div className="space-y-1.5">
              <label htmlFor="rc-person" className="text-sm font-medium">Person</label>
              <SearchableSelect
                id="rc-person"
                value={userId}
                onChange={setUserId}
                allLabel="Choose a person..."
                allValue=""
                searchPlaceholder="Type a name..."
                options={info.candidates
                  .filter((c) => !info.rules.some((r) => r.userId === c.userId))
                  .map((c) => ({ value: c.userId, label: c.name }))}
              />
            </div>
          )}

          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Documents</legend>
            <label htmlFor="rc-alldocs" className="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm">
              <input id="rc-alldocs" type="checkbox" className="h-4 w-4" checked={allDocs} onChange={() => setAllDocs(!allDocs)} />
              Every document
            </label>
            {!allDocs &&
              info.docKinds.map((k) => (
                <label key={k.key} htmlFor={`rc-k-${k.key}`} className="ml-4 flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm">
                  <input id={`rc-k-${k.key}`} type="checkbox" className="h-4 w-4" checked={docTypes.includes(k.key)} onChange={() => toggle(docTypes, setDocTypes, k.key)} />
                  {k.label}
                </label>
              ))}
          </fieldset>

          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Sites</legend>
            <label htmlFor="rc-allsites" className="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm">
              <input id="rc-allsites" type="checkbox" className="h-4 w-4" checked={allSites} onChange={() => setAllSites(!allSites)} />
              Every site they can see
            </label>
            {!allSites && (
              <div className="ml-4 space-y-1.5">
                <input
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  placeholder="Search sites..."
                  aria-label="Search sites"
                  className="h-10 w-full rounded-md border bg-background px-3 text-base md:text-sm"
                />
                <div className="max-h-56 space-y-1 overflow-y-auto">
                  {shownSites.map((p) => (
                    <label key={p.id} htmlFor={`rc-s-${p.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm">
                      <input id={`rc-s-${p.id}`} type="checkbox" className="h-4 w-4" checked={projectIds.includes(p.id)} onChange={() => toggle(projectIds, setProjectIds, p.id)} />
                      <span className="min-w-0 truncate">{p.name}</span>
                    </label>
                  ))}
                  {shownSites.length === 0 && <p className="text-xs text-muted-foreground">No sites match.</p>}
                </div>
              </div>
            )}
          </fieldset>
          <p className="text-xs text-muted-foreground">
            A division admin only ever receives their own division's documents, a foreman only the sites they are foreman on, and a crew member only their division's sites.
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" disabled={!userId || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
